<script setup lang="ts">
import { computed, ref, onMounted, inject } from 'vue'
import { useSessionStore } from '@/store/sessionStore'
import axiosInstance from '@/plugins/axios'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

const sessionStore = useSessionStore()
const customTranslations = ref<any[]>([])
const gF = inject('gF') as any
const { t } = useI18n()
const tenantDetails = ref({})
const toast = useToast()
const allTranslations = ref([])
const languageCodes = ref<{ [key: string]: string }>({})
const defaultLanguages = ref(['670e1e32d4634dd3c3ec7478', '670fad078be48456eb53b250'])

const getCustomTranslations = async () => {
  try {
    for (const lang of tenant.value.adminSettings.enabledLanguages) {
      const response = await axiosInstance.get(`/translations/${lang}`)
      if (
        response.data.languageIdentifier === 'ailab-general-de' ||
        response.data.languageIdentifier === 'ailab-general-en'
      ) {
        continue
      }
      customTranslations.value.push(response.data)
    }
  } catch (error) {
    console.log(error)
  }
}

const tenant = computed(
  () =>
    sessionStore.getTenant() || {
      id: '',
      name: '',
      domains: [],
      adminSettings: {
        enabledLanguages: []
      },
      settings: {
        active: false,
        defaultLanguage: '',
        reportsEndpoint: ''
      }
    }
)

const toggleTranslationStatus = async (translationId: any) => {
  const translation = customTranslations.value.find((t) => t._id === translationId)
  const status = translation?.active

  if (translation.status && translation?.progress?.missingRequiredFields > 0) {
    toast.add({
      severity: 'warn',
      summary: 'Missing required fields',
      detail: 'This translation is missing required fields and cannot be activated.'
    })
    return
  }

  try {
    const res = await axiosInstance.put(`/translations/toggleTranslationStatus/${translationId}`)
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: res.data.message
    })
  } catch (error: any) {
    if (translation) translation.active = !status
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || error.message || 'Unexpected error'
    })
  }
}

const checkPermission = (state: boolean, progress: any) => {
  if (!state) {
    return progress?.missingRequiredFields > 0
  }
}

const getAllTranslations = async () => {
  const res = await axiosInstance.get('/admin/getAllTranslationsMinified')
  allTranslations.value = res.data
}
const fetchLanguageCodes = async () => {
  for (const langId of tenant.value.adminSettings.enabledLanguages) {
    const res = await axiosInstance.get(`/translations/${langId}`)
    languageCodes.value[langId] = res.data.languageCode
  }
}
onMounted(async () => {
  gF.startLoading()
  try {
    //await getTenantDetails();
    tenantDetails.value = sessionStore.tenant || {}

    await getAllTranslations()
    await fetchLanguageCodes()
    await getCustomTranslations()
  } catch (error) {
    console.error('Error fetching tenant details:', error)
  } finally {
    gF.endLoading()
  }
})
</script>

<template>
  <SettingsPageTemplate>
    <template #settings-content>
      <div class="card p-3 mt-3">
        <h5>Custom Translations</h5>
        <DataTable
          :value="customTranslations"
          :scrollable="true"
          scrollHeight="400px"
          class="customTranslationsTable"
        >
          <Column field="languageIdentifier" header="Language Identifier" />
          <Column field="progress" header="Required Translations">
            <template #body="{ data }">
              <ProgressBar
                :value="
                  Math.round(
                    ((data.progress.requiredFieldsTotal - data.progress.missingRequiredFields) /
                      data.progress.requiredFieldsTotal) *
                      100
                  )
                "
                :show-value="
                  data.progress.requiredFieldsTotal - data.progress.missingRequiredFields > 200
                "
              >
                {{ data.progress.requiredFieldsTotal - data.progress.missingRequiredFields }}
                / {{ data.progress.requiredFieldsTotal }}
              </ProgressBar>
            </template>
          </Column>

          <Column field="progress" header="Optional Translations">
            <template #body="{ data }">
              <ProgressBar
                :value="
                  data.progress.optionalFieldsTotal > 0
                    ? Math.round(
                        (data.progress.translatedOptionalFields /
                          data.progress.optionalFieldsTotal) *
                          100
                      )
                    : 0
                "
                :showValue="false"
              >
                {{ data.progress.translatedOptionalFields }}
                / {{ data.progress.optionalFieldsTotal }}
              </ProgressBar>
            </template>
          </Column>

          <Column field="active" header="Active">
            <template #body="{ data }">
              <ToggleButton
                @click="toggleTranslationStatus(data._id)"
                v-model="data.active"
                :disabled="checkPermission(data.active, data.progress)"
                onLabel="Active"
                offLabel="Inactive"
                class="w-100"
              />
            </template>
          </Column>
        </DataTable>
      </div>
    </template>

    <template #actions-right>
      <Button
        label="Übersetzungen verwalten"
        icon="pi pi-globe"
        :to="{ path: '/tenant/settings/i18n/static-texts' }"
      />
    </template>

    <TenantStaticTextsOverview />
  </SettingsPageTemplate>
</template>
